/* eslint-disable react/prop-types */
import axios from 'axios';
import Swal from 'sweetalert2';
import Cookies from 'js-cookie';
import { useTranslation } from 'react-i18next';
import ReCAPTCHA from 'react-google-recaptcha';
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import PasswordStrengthBar from 'react-password-strength-bar';
// api
import BaseApi from '../api/BaseApi';
// components
import NavBar from '../element/NavBar';
import Footer from '../element/Footer';
import SmallLoading from '../element/SmallLoading';
// mui
import { Tooltip } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

const EmployerRegister = ({ captchaKey }) => {
  const navigate = useNavigate();
  const { t } = useTranslation('global');
  const userType = Cookies.get('user_type');
  const tokenClient = Cookies.get('tokenClient');
  const currentLanguage = Cookies.get('selectedLanguage') || 'ukr';
  const recaptchaLanguage = currentLanguage === 'ukr' ? 'uk' : currentLanguage;

  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isCaptchaVerified, setIsCaptchaVerified] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [registerData, setRegisterData] = useState({
    email: '',
    contact: '',
    password: '',
    last_name: '',
    first_name: '',
    company_name: '',
    confirm_password: '',
    user_type: 'recruiter',
  });
  const [errors, setErrors] = useState({
    email: '',
    contact: '',
    captcha: '',
    password: '',
    last_name: '',
    first_name: '',
    company_name: '',
    confirm_password: '',
  });

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleSubmit = async e => {
    e.preventDefault();

    try {
      const newErrors = {};

      const isValidEmail = email => {
        const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        return emailPattern.test(email);
      };

      const isValidContact = contact => {
        const contactPattern = /^\+?3?8?(0\d{9})$/;

        return contactPattern.test(contact);
      };

      if (registerData.company_name === '') {
        newErrors.company_name = t('employerRegister.companyRequired');
        window.scrollTo(0, 0);
      }
      if (registerData.first_name === '') {
        newErrors.first_name = t('employerRegister.firstNameRequired');
        window.scrollTo(0, 0);
      }
      if (registerData.last_name === '') {
        newErrors.last_name = t('employerRegister.lastNameRequired');
        window.scrollTo(0, 0);
      }
      if (registerData.email === '') {
        newErrors.email = t('employerRegister.emailRequired');
        window.scrollTo(0, 0);
      } else if (!isValidEmail(registerData.email)) {
        newErrors.email = t('employerRegister.invalidEmail');
        window.scrollTo(0, 0);
      }
      if (registerData.contact === '') {
        newErrors.contact = t('employerRegister.contactRequired');
        window.scrollTo(0, 0);
      } else if (!isValidContact(registerData.contact)) {
        newErrors.contact = t('employerRegister.invalidContact');
        window.scrollTo(0, 0);
      }
      if (registerData.password === '') {
        newErrors.password = t('employerRegister.passwordRequired');
        window.scrollTo(0, 0);
      }
      if (registerData.confirm_password === '') {
        newErrors.confirm_password = t('employerRegister.confirmPasswordRequired');
        window.scrollTo(0, 0);
      }
      if (registerData.password) {
        if (registerData.password.length < 8) {
          newErrors.password = t('employerRegister.passwordLengthError');
          window.scrollTo(0, 0);
        }
      }
      if (registerData.confirm_password) {
        if (registerData.confirm_password.length < 8) {
          newErrors.confirm_password = t('employerRegister.passwordLengthError');
          window.scrollTo(0, 0);
        }
      }
      if (registerData.confirm_password && registerData.confirm_password) {
        if (registerData.password != registerData.confirm_password) {
          newErrors.confirm_password = t('employerRegister.passwordMatchError');
          window.scrollTo(0, 0);
        }
      }
      if (!isCaptchaVerified) {
        newErrors.captcha = t('employerRegister.captchaRequired');
      }

      setErrors(newErrors);

      if (Object.keys(newErrors).length === 0) {
        if (isCaptchaVerified) {
          setLoading(true);

          const updatedData = {
            ...registerData,
            language: currentLanguage,
          };
          const response = await axios.post(
            BaseApi + '/users/registration',
            updatedData,
          );
          const status = response.data.status;

          if (status === 200) {
            Swal.fire({
              icon: 'success',
              confirmButtonText: t('employerRegister.close'),
              title: t('employerRegister.creationSuccessMessage'),
            });

            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
              'event': 'registration',
              'authentication_method': 'email',
              'user_email': response.data.response.email_address,
            });

            navigate('/user/employerlogin');

            setIsCaptchaVerified(false);
          } else {
            Swal.fire({
              icon: 'error',
              title: response.data.message.substring(2),
              confirmButtonText: t('employerRegister.close'),
            });

            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
              'event': 'website_error',
              'error_type': 'registration error',
              'error_message': response.data.message.substring(2),
            });

            setIsCaptchaVerified(false);
          }

          setLoading(false);
        }
      }
    } catch (error) {
      setLoading(false);

      Swal.fire({
        icon: 'error',
        title: t('employerRegister.failedTitle'),
        text: t('employerRegister.failedMessage'),
        confirmButtonText: t('employerRegister.close'),
      });

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        'event': 'website_error',
        'error_message': error.message,
        'error_type': 'registration error',
      });

      setIsCaptchaVerified(false);
    }
  };

  const handleChange = e => {
    const { name, value } = e.target;

    setRegisterData(prevData => ({
      ...prevData,
      [name]: value,
    }));
    setErrors(prev => ({
      ...prev,
      [name]: '',
    }));
  };

  const handleReset = e => {
    e.preventDefault();

    setRegisterData({
      email: '',
      password: '',
      last_name: '',
      first_name: '',
      company_name: '',
      confirm_password: '',
    });
  };

  useEffect(() => {
    if (tokenClient && userType === 'recruiter' || tokenClient && userType === 'candidate') {
      navigate('/');

      const Toast = Swal.mixin({
        toast: true,
        timer: 3000,
        position: 'top-end',
        timerProgressBar: true,
        showConfirmButton: false,
        didOpen: toast => {
          toast.addEventListener('mouseenter', Swal.stopTimer);
          toast.addEventListener('mouseleave', Swal.resumeTimer);
        },
      });

      Toast.fire({
        icon: 'warning',
        title: t('employerRegister.alreadyLoggedIn'),
      });
    }

    const metaTitle = document.querySelector('title');
    const metaDescription = document.querySelector('meta[name="description"]');

    if (metaTitle) {
      metaTitle.textContent = t('employerRegister.metaTitleValue');
    }

    if (metaDescription) {
      metaDescription.content = t('employerRegister.metaDescriptionValue');
    }

    window.scrollTo(0, 0);
  }, [t]); // eslint-disable-line

  return (
    <>
      <NavBar />
      {
        loading
          ? (
            <div className='loader-container'></div>
          ) : (
            <>
              <div className='container employerLogin EPRegistration'>
                <div className='card rounded'>
                  <div className='row'>
                    <div className='col-md-6 leftSection'>
                      <img src='/Images/employerlogin.jpg' alt='' />
                    </div>
                    <div className='col-md-6 mt-5'>
                      <div className='text-center'>
                        <img src='/Images/Logo.png' alt='logo' width='50px' />
                      </div>
                      <div className='card-title text-center h3 pt-5'>
                        <h1 style={{ fontSize: '28px' }}>{t('employerRegister.empAcRegistration')}</h1>
                      </div>
                      <div className='card-body'>
                        <form className='border border-light'>
                          <div className='mb-4'>
                            <input
                              type='text'
                              id='company_name'
                              name='company_name'
                              onChange={handleChange}
                              value={registerData.company_name}
                              placeholder={t('employerRegister.companyName')}
                              className={`form-control ${errors.company_name && 'input-error'}`}
                            />
                            {
                              errors.company_name && (
                                <div className='text-danger'>
                                  {errors.company_name}
                                </div>
                              )
                            }
                          </div>
                          <div className='mb-4'>
                            <input
                              type='text'
                              id='first_name'
                              name='first_name'
                              onChange={handleChange}
                              value={registerData.first_name}
                              placeholder={t('employerRegister.firstName')}
                              className={`form-control ${errors.first_name && 'input-error'}`}
                            />
                            {
                              errors.first_name && (
                                <div className='text-danger'>{errors.first_name}</div>
                              )
                            }
                          </div>
                          <div className='mb-4'>
                            <input
                              type='text'
                              id='last_name'
                              name='last_name'
                              onChange={handleChange}
                              value={registerData.last_name}
                              placeholder={t('employerRegister.lastName')}
                              className={`form-control ${errors.last_name && 'input-error'}`}
                            />
                            {
                              errors.last_name && (
                                <div className='text-danger'>{errors.last_name}</div>
                              )
                            }
                          </div>
                          <div className='mb-4'>
                            <input
                              id='email'
                              name='email'
                              type='email'
                              onChange={handleChange}
                              value={registerData.email}
                              placeholder={t('employerRegister.email')}
                              className={`form-control ${errors.email && 'input-error'}`}
                            />
                            {
                              errors.email && (
                                <div className='text-danger'>{errors.email}</div>
                              )
                            }
                          </div>
                          <div className='mb-4'>
                            <input
                              type='tel'
                              id='contact'
                              name='contact'
                              onChange={handleChange}
                              value={registerData.contact}
                              placeholder={t('employerRegister.contact')}
                              className={`form-control ${errors.contact && 'input-error'}`}
                            />
                            {
                              errors.contact && (
                                <div className='text-danger'>{errors.contact}</div>
                              )
                            }
                          </div>
                          <div className='mb-4 passwordBox'>
                            <input
                              id='password'
                              name='password'
                              onChange={handleChange}
                              value={registerData.password}
                              type={showPassword ? 'text' : 'password'}
                              placeholder={t('employerRegister.password')}
                              className={`form-control ${errors.password && 'input-error'}`}
                            />
                            <div className='passwordVisibility'>
                              <p
                                type='button'
                                className='btn-primary'
                                onClick={togglePasswordVisibility}
                              >
                                {
                                  showPassword
                                    ? (
                                      <>
                                        <Tooltip title='Hide Password'>
                                          <VisibilityOffIcon />
                                        </Tooltip>
                                      </>
                                    ) : (
                                      <>
                                        <Tooltip title='View Password'>
                                          <VisibilityIcon />
                                        </Tooltip>
                                      </>
                                    )
                                }
                              </p>
                            </div>
                            {
                              errors.password && (
                                <div className='text-danger'>{errors.password}</div>
                              )
                            }
                            {
                              registerData.password && (
                                <PasswordStrengthBar
                                  password={registerData.password}
                                />
                              )
                            }
                          </div>
                          <div className='mb-4 passwordBox'>
                            <input
                              id='confirm_password'
                              name='confirm_password'
                              onChange={handleChange}
                              value={registerData.confirm_password}
                              type={showConfirmPassword ? 'text' : 'password'}
                              placeholder={t('employerRegister.confirmPassword')}
                              className={`form-control ${errors.confirm_password && 'input-error'}`}
                            />
                            <div className='passwordVisibility'>
                              <p
                                type='button'
                                className='btn-primary'
                                onClick={toggleConfirmPasswordVisibility}
                              >
                                {
                                  showConfirmPassword
                                    ? (
                                      <>
                                        <Tooltip title='Hide Password'>
                                          <VisibilityOffIcon />
                                        </Tooltip>
                                      </>
                                    ) : (
                                      <>
                                        <Tooltip title='View Password'>
                                          <VisibilityIcon />
                                        </Tooltip>
                                      </>
                                    )
                                }
                              </p>
                            </div>
                            {
                              errors.confirm_password && (
                                <div className='text-danger'>
                                  {errors.confirm_password}
                                </div>
                              )
                            }
                            {
                              registerData.confirm_password && (
                                <PasswordStrengthBar
                                  password={registerData.confirm_password}
                                />
                              )
                            }
                          </div>
                          <div className='reCaptchaLogin'>
                            {
                              captchaKey
                                ? <ReCAPTCHA sitekey={captchaKey} hl={recaptchaLanguage} onChange={value => setIsCaptchaVerified(value)} />
                                : <SmallLoading />
                            }
                            {
                              errors.captcha && (
                                <div className='text-danger CaptchaVerify'>
                                  {errors.captcha}
                                </div>
                              )
                            }
                          </div>
                          <div className='d-flex justify-content-evenly text-center'>
                            <button
                              type='submit'
                              onClick={handleSubmit}
                              className='btn button1 my-4'
                            >
                              {t('employerRegister.submitButton')}
                            </button>
                            <button
                              type='button'
                              onClick={handleReset}
                              className='btn button2 my-4'
                            >
                              {t('employerRegister.resetButton')}
                            </button>
                          </div>
                          <p className='EPRegistrationBottomText'>
                            {t('employerRegister.bottomTxt1')}
                            {' '}
                            <Link to='/terms-of-use'>
                              {t('employerRegister.bottomTxt2')}
                            </Link>
                            {', '}
                            {t('employerRegister.bottomTxt7')}
                            {' '}
                            <Link to='/offer-agreement'>
                              {t('employerRegister.bottomTxt8')}
                            </Link>
                            {' '}
                            {t('employerRegister.bottomTxt5')}
                            {' '}
                            <Link to='/data-processing-consent'>
                              {t('employerRegister.bottomTxt6')}
                            </Link>
                          </p>
                          <p className='EPRegistrationBottomText'>
                            {t('employerRegister.bottomTxt3')}
                            {' '}
                            <Link to='/user/employerlogin'>
                              {t('employerRegister.bottomTxt4')}
                            </Link>
                          </p>
                        </form>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <Footer />
            </>
          )
      }
    </>
  );
};

export default EmployerRegister;
